import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullQualifiedClientId from '../../../../Various/FullQualifiedClientId';
import ActionButton from '../shared/ActionButton';
import CampaignPerformanceHeader from './CampaignPerformanceHeader';
import PerformanceDataView from './PerformanceDataView';
import useStickyState from '../../../../../functions/useStickyState';

const CampaignPerformanceTab = ({ 
    selectedCampaignId,
    wsProvider,
    clientCampaignsTree,
    clientCampaignsOverview
}) => {
    const [performanceData, setPerformanceData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Use sticky state to persist date range values across page refreshes
    const [dateRangeType, setDateRangeType] = useStickyState("last_period", "campaignPerformance_dateRangeType"); // "range", "last_period", "this_period", or "latest_first_week"
    const [dateFrom, setDateFrom] = useStickyState("2025-11-01", "campaignPerformance_dateFrom");
    const [dateTo, setDateTo] = useStickyState("2025-11-24", "campaignPerformance_dateTo");
    const loadingRef = useRef(false);
    const wsProviderRef = useRef(wsProvider);
    const clientCampaignsTreeRef = useRef(clientCampaignsTree);
    const clientCampaignsOverviewRef = useRef(clientCampaignsOverview);

    // Update refs when props change
    useEffect(() => {
        wsProviderRef.current = wsProvider;
        clientCampaignsTreeRef.current = clientCampaignsTree;
        clientCampaignsOverviewRef.current = clientCampaignsOverview;
    }, [wsProvider, clientCampaignsTree, clientCampaignsOverview]);

    // Helper function to check if campaigns data exists
    const hasCampaignsData = useCallback((tree, overview) => {
        const treeData = tree || clientCampaignsTreeRef.current;
        const overviewData = overview || clientCampaignsOverviewRef.current;
        return (treeData && Object.keys(treeData).length > 0) ||
               (overviewData && Object.keys(overviewData).length > 0);
    }, []);

    // Helper function to search for campaign in a data structure
    const searchCampaignInData = useCallback((data, campaignId) => {
        if (!data || Object.keys(data).length === 0) {
            return null;
        }
        for (const [clientId, clientArray] of Object.entries(data)) {
            if (clientArray[0]?.campaigns?.some(campaign => campaign.campaign_id === campaignId)) {
                return clientId;
            }
        }
        return null;
    }, []);

    // Helper function to find which client a campaign belongs to
    const findCampaignClient = useCallback((campaignId) => {
        // Try to find in tree data first
        const treeClient = searchCampaignInData(clientCampaignsTreeRef.current, campaignId);
        if (treeClient) {
            return treeClient;
        }
        // Fallback to overview data
        return searchCampaignInData(clientCampaignsOverviewRef.current, campaignId);
    }, [searchCampaignInData]);

    // Helper function to build full client ID
    const buildFullClientId = useCallback((campaignClient, provider) => {
        const campaignClientData = provider.clients.filter(x => x.client === campaignClient)[0];
        if (campaignClientData) {
            return (new FullQualifiedClientId(
                campaignClientData["project"], 
                campaignClientData["projectPath"], 
                campaignClientData["client"]
            )).toString();
        }
        // Fallback: construct from current project info
        return (new FullQualifiedClientId(
            provider.selectedProjectId, 
            provider.selectedProjectPath, 
            campaignClient
        )).toString();
    }, []);

    const loadPerformanceData = useCallback(() => {
        const provider = wsProviderRef.current;
        if (!selectedCampaignId || !provider) {
            return;
        }

        // Prevent multiple simultaneous loads
        if (loadingRef.current) {
            return;
        }

        setIsLoading(true);
        setError(null);
        loadingRef.current = true;

        // Find which client this campaign belongs to
        const campaignClient = findCampaignClient(selectedCampaignId);
        if (!campaignClient) {
            setError('Could not determine campaign client');
            setIsLoading(false);
            loadingRef.current = false;
            return;
        }

        // Build the full client ID
        const fullClientId = buildFullClientId(campaignClient, provider);

        // Build options based on selected date range type
        const options = {
            campaign_id: selectedCampaignId,
            client_id: fullClientId
        };

        if (dateRangeType === "range") {
            // date_range_type: "range" uses date_from and date_to
            options.date_range_type = "range";
            options.date_from = dateFrom;
            options.date_to = dateTo;
        } else {
            // date_range_type: "term" uses date_range_term with fixed logical inputs
            // Options: "last_period", "this_period", or "latest_first_week"
            options.date_range_type = "term";
            options.date_range_term = dateRangeType;
        }

        provider.ws.waitForSocketAndSend({
            action: "getCampaignPerformanceAnalysis",
            options: options
        }, (response) => {
            setIsLoading(false);
            loadingRef.current = false;
            if (response.error) {
                setError(response.error);
            } else {
                setPerformanceData(response);
            }
        });
    }, [selectedCampaignId, findCampaignClient, buildFullClientId, dateRangeType, dateFrom, dateTo]);

    useEffect(() => {
        if (selectedCampaignId) {
            // Wait for campaigns data to be available before trying to load performance data
            if (hasCampaignsData(clientCampaignsTree, clientCampaignsOverview)) {
                loadPerformanceData();
            } else {
                // If campaigns data isn't loaded yet, wait a bit and retry
                const timeoutId = setTimeout(() => {
                    if (hasCampaignsData() && selectedCampaignId) {
                        loadPerformanceData();
                    }
                }, 1000);
                return () => clearTimeout(timeoutId);
            }
        } else {
            setPerformanceData(null);
            setError(null);
        }
    }, [selectedCampaignId, clientCampaignsTree, clientCampaignsOverview, loadPerformanceData, hasCampaignsData]);

    if (!selectedCampaignId) {
        return (
            <div className="text-center p-4">
                <p>Please select a campaign to view performance analysis</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{ padding: '16px' }}>
                <CampaignPerformanceHeader
                    selectedCampaignId={selectedCampaignId}
                    dateRangeType={dateRangeType}
                    onDateRangeTypeChange={setDateRangeType}
                    dateFrom={dateFrom}
                    onDateFromChange={setDateFrom}
                    dateTo={dateTo}
                    onDateToChange={setDateTo}
                />
                <div className="text-center p-4">
                    <p>Loading performance analysis...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '16px' }}>
                <CampaignPerformanceHeader
                    selectedCampaignId={selectedCampaignId}
                    dateRangeType={dateRangeType}
                    onDateRangeTypeChange={setDateRangeType}
                    dateFrom={dateFrom}
                    onDateFromChange={setDateFrom}
                    dateTo={dateTo}
                    onDateToChange={setDateTo}
                />
                <div className="text-center p-4">
                    <p style={{ color: '#dc3545' }}>Error: {error}</p>
                    <div style={{ marginTop: '8px' }}>
                        <ActionButton label="Retry" onClick={loadPerformanceData} variant="primary" size="md" />
                    </div>
                </div>
            </div>
        );
    }

    if (!performanceData) {
        return (
            <div style={{ padding: '16px' }}>
                <CampaignPerformanceHeader
                    selectedCampaignId={selectedCampaignId}
                    dateRangeType={dateRangeType}
                    onDateRangeTypeChange={setDateRangeType}
                    dateFrom={dateFrom}
                    onDateFromChange={setDateFrom}
                    dateTo={dateTo}
                    onDateToChange={setDateTo}
                />
                <div className="text-center p-4">
                    <p>No performance data available</p>
                    <div style={{ marginTop: '8px' }}>
                        <ActionButton label="Load Data" onClick={loadPerformanceData} variant="primary" size="md" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px' }}>
            <CampaignPerformanceHeader
                selectedCampaignId={selectedCampaignId}
                dateRangeType={dateRangeType}
                onDateRangeTypeChange={setDateRangeType}
                dateFrom={dateFrom}
                onDateFromChange={setDateFrom}
                dateTo={dateTo}
                onDateToChange={setDateTo}
            />
            <PerformanceDataView performanceData={performanceData} />
        </div>
    );
};

export default CampaignPerformanceTab;

